import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ErrorState } from "../../components/common/ErrorState";
import { EMULATOR } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";
import { fadeUp } from "../../lib/motion";

// UX-CP-03 SCR-P09-00/01/02/03 "Emulator entry — select gym, persona, reason". §1.5/NFR-015:
// permission-gated entry, blocked with a clear message (not a silent hide) if the operator
// lacks `emulate` — CP-02's sidebar already hides the nav item, this is the second gate at the
// action itself, same defense-in-depth pattern as Monetization's client-side `hasBilling` check.
export default function EmulatorEntry() {
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const { me } = useKairooData();
  const canEmulate = (me?.kairoo_permissions || []).includes("emulate");

  const [orgId, setOrgId] = useState("");
  const [persona, setPersona] = useState("");
  const [reason, setReason] = useState("");

  const gymsQ = useQuery({
    queryKey: ["control-gyms"],
    queryFn: async () => (await api.get("/control/gyms")).data.gyms,
    enabled: canEmulate,
  });
  const personasQ = useQuery({
    queryKey: ["gym-personas", orgId],
    queryFn: async () => (await api.get(`/control/gyms/${orgId}/personas`)).data.personas,
    enabled: canEmulate && !!orgId,
  });

  const startMutation = useMutation({
    mutationFn: () => api.post("/control/emulator/start", { organization_id: orgId, persona, reason }),
    onSuccess: () => {
      const chosen = (personasQ.data || []).find((p) => p.persona === persona);
      // BUG FIX: this used to hardcode `/app/dashboard` for EVERY persona with a surface - Front
      // Desk/Manager landed on the Owner Cockpit shell (wrong permissions for most of it, and
      // NOT where DeskShell's own EmulationFlag ribbon lives), Trainer landed there too. Route
      // each persona to the shell it actually belongs in.
      const landingPath = {
        super_admin: "/app/dashboard", front_of_house_fd: "/desk/today", front_of_house_mgr: "/desk/today",
        trainer: "/trainer/today",
      }[persona];
      navigate(chosen?.has_app_surface && landingPath ? landingPath : "/emulate/unavailable");
    },
  });

  if (!canEmulate) {
    return (
      <div data-testid={EMULATOR.entryPage} className="max-w-2xl">
        <div className="solid-surface rounded-[14px] p-16 flex flex-col items-center text-center" data-testid={EMULATOR.forbidden}>
          <ShieldAlert size={28} style={{ color: "var(--status-overdue-text)" }} className="mb-3" />
          <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>Emulator requires the 'emulate' permission</p>
          <p className="text-sm mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Ask a platform operator with emulate access to grant it, or view a section you already have access to.</p>
        </div>
      </div>
    );
  }

  if (gymsQ.isError) return <ErrorState error={gymsQ.error} onRetry={() => gymsQ.refetch()} title="Couldn't load gyms" />;

  const canEnter = orgId && persona && reason.trim().length > 0 && !startMutation.isPending;

  return (
    <div data-testid={EMULATOR.entryPage} className="max-w-xl space-y-6 pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Emulator</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
          Step into a gym's own screens to diagnose or fix an issue directly — every action is
          automatically and irrefutably attributed to you, never to the gym's real staff.
        </p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="solid-surface rounded-[14px] p-5 space-y-4">
        <div>
          <p className="eyebrow mb-1.5">Gym</p>
          <Select value={orgId} onValueChange={(v) => { setOrgId(v); setPersona(""); }}>
            <SelectTrigger data-testid={EMULATOR.gymSelect}><SelectValue placeholder="Choose a gym…" /></SelectTrigger>
            <SelectContent>
              {(gymsQ.data || []).map((g) => <SelectItem key={g.organization_id} value={g.organization_id}>{g.display_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <p className="eyebrow mb-1.5">Persona</p>
          <Select value={persona} onValueChange={setPersona} disabled={!orgId}>
            <SelectTrigger data-testid={EMULATOR.personaSelect}><SelectValue placeholder="Choose a persona…" /></SelectTrigger>
            <SelectContent>
              {(personasQ.data || []).map((p) => (
                <SelectItem key={p.persona} value={p.persona} data-testid={EMULATOR.personaOption(p.persona)}>
                  {p.label}{!p.has_app_surface ? " (screens not yet available)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <p className="eyebrow mb-1.5">Reason (required)</p>
          <Textarea
            data-testid={EMULATOR.reasonInput}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Investigating a member complaint about a failed WhatsApp reminder"
            rows={3}
          />
        </div>

        {startMutation.isError && (
          <p data-testid={EMULATOR.enterError} className="text-sm" style={{ color: "var(--status-overdue-text)" }}>
            {formatApiError(startMutation.error).message}
          </p>
        )}

        <Button
          data-testid={EMULATOR.enterButton}
          disabled={!canEnter}
          className="w-full text-white"
          style={{ background: "var(--scarlet-accent)" }}
          onClick={() => startMutation.mutate()}
        >
          {startMutation.isPending ? "Starting…" : "Enter emulation"}
        </Button>
      </motion.div>
    </div>
  );
}
